/** @jsx jsx */
import PropTypes from 'prop-types';
import { useState, Fragment, useContext, useRef, useEffect } from 'react';
import { Button, Flex, Spinner, jsx } from 'theme-ui';
import { useCart, useTranslate } from '@chordcommerce/gatsby-theme-autonomy';
import { navigate } from 'gatsby';
import MessageContext from '~/contexts/MessageContext';
import useOnClickOutside from '~/hooks/components/use-on-click-outside';
import CartContext from '~/contexts/CartContext';
import updateCreatorIQMetric from '~/utils/misc/updateCreatorIQMetric';

const ProductAddToCartButton = ({
  soldOut,
  comingSoon = false,
  disabled = false,
  buttonName = null,
  price,
  quantity,
  regularPrice,
  sku,
  currentVariant,
  handleAddToCart,
  subscription,
  slug,
  currentVariantInStock = 1,
  name = '',
  interval = null,
  forwardSX = {},
  forwardButtonSX = {},
  hideOptions = false,
  buttonCTAVariant = false,
  showPrice = null,
  isGiftCard,
  giftNoteAttributes = {},
  resetGiftNoteAttributes = () => {},
  isFetchingAvailability = false,
  ...props
}) => {
  const buttonRef = useRef();
  const translate = useTranslate();
  const { addToCart, addSubscription, cart, modifyGiftCards } = useCart();
  const [buttonIsLoading, setButtonIsLoading] = useState(false);
  const [, setCartIsOpen] = useContext(CartContext);
  const [pendingGiftNoteAttributes, setPendingGiftNoteAttributes] = useState();

  useOnClickOutside(buttonRef);

  const [, setMessage] = useContext(MessageContext);

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (disabled || !currentVariantInStock) return;
    setButtonIsLoading(true);
    setMessage(null);
    const findInCart = cart?.lineItems?.find((item) => item.variant.sku === sku);
    const quantityInCart = findInCart?.quantity || 0;
    try {
      if (subscription && interval) {
        await addSubscription({
          sku,
          quantity,
          endDate: null,
          interval: {
            length: interval.length,
            unit: interval.unit[0],
          },
        });
        await updateCreatorIQMetric();
      } else if (quantityInCart >= currentVariantInStock) {
        const error = `Sorry! Only ${currentVariantInStock} units of ${name} are currently available.`;
        setMessage(error);
      } else {
        await addToCart({ sku, quantity });

        if (isGiftCard) {
          setPendingGiftNoteAttributes(giftNoteAttributes);
        }

        if (typeof window !== 'undefined') {
          window.ttq.track('AddToCart', { value: cart.total, currency: 'USD' });
        }
      }
      await updateCreatorIQMetric();
      setCartIsOpen(true);
    } catch (error) {
      console.log(error);
      setMessage(translate('error.api.default'));
    }

    setButtonIsLoading(false);
    setTimeout(() => {
      setMessage(null);
    }, 4000);
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window?.location?.search);
      if (urlParams.has('InstantAddToCart') && !hideOptions) {
        handleSubmit();
      }
      navigate(window?.location?.pathname);
    }
  }, []);

  useEffect(() => {
    const lineItem = cart.lineItems?.find((item) => item.variant.sku === sku);
    const giftCard = lineItem?.giftCards.find((c) => !c.recipientEmail) ?? [];

    if (giftCard && pendingGiftNoteAttributes?.recipientEmail) {
      modifyGiftCards({
        giftCards: {
          giftCardsDetails: [
            {
              id: giftCard.id,
              recipientEmail: pendingGiftNoteAttributes.recipientEmail,
              recipientName: pendingGiftNoteAttributes.recipientName,
              giftMessage: pendingGiftNoteAttributes.message,
              sendEmailAt: pendingGiftNoteAttributes.sendOn,
            },
          ],
        },
      }).then(() => resetGiftNoteAttributes());

      setPendingGiftNoteAttributes(undefined);
    }
  }, [cart.lineItems, pendingGiftNoteAttributes]);

  return (
    <form
      className="visibleOnHover"
      onSubmit={(e) => handleSubmit(e)}
      sx={{
        position: 'relative',
        width: '100%',
        height: '50px',
        mt: ['50px', '60px', '1.5rem'],
        mb: ['50px', '60px', '5.5rem'],
        zIndex: 4,
        opacity: 1,
        display: 'flex',
        justifyContent: ['center', 'flex-start', 'flex-start'],
        backgroundColor: 'transparent',
        transition: 'opacity 0.25s ease',
        ...forwardSX,
      }}
      ref={buttonRef}
    >
      <Button
        disabled={
          disabled ||
          !currentVariantInStock ||
          hideOptions ||
          (isGiftCard && !giftNoteAttributes.recipientEmail)
        }
        {...props}
        p={0}
        variant={buttonCTAVariant ? 'buttons.ctaHighlight' : ''}
        type="submit"
        sx={{
          position: 'relative',
          zIndex: 1,
          outline: 'none',
          variant: 'buttons.product',
          borderRadius: '16px',
          width: '182px',
          height: '39px !important',
          fontSize: '13px !important',
          justifyContent: 'center',
          ...forwardButtonSX,
        }}
      >
        <Flex sx={{ height: '100%' }}>
          <Flex
            sx={{
              alignItems: 'center',
              flexGrow: 1,
              height: '100%',
              justifyContent: 'center',
              padding: ['0 32px', null, '0 32px'],
            }}
          >
            {(buttonIsLoading || isFetchingAvailability) && (
              <Spinner data-testid="spinner" size="15" color="inherit" />
            )}
            {!buttonIsLoading && !isFetchingAvailability && (
              <Fragment>
                {buttonName || ''}
                {comingSoon && translate('product.coming_soon')}
                {!currentVariantInStock && !hideOptions && translate('product.sold_out')}
                {!!currentVariantInStock &&
                  !hideOptions &&
                  !buttonName &&
                  `ADD TO BAG ${showPrice ? ` - $${showPrice.toFixed(2)}` : ''}`}
                {hideOptions && 'Already In Cart'}
              </Fragment>
            )}
          </Flex>
        </Flex>
      </Button>
    </form>
  );
};

ProductAddToCartButton.propTypes = {
  soldOut: PropTypes.bool,
  comingSoon: PropTypes.bool,
  disabled: PropTypes.bool,
  price: PropTypes.number,
  quantity: PropTypes.number.isRequired,
  regularPrice: PropTypes.number,
  sku: PropTypes.string.isRequired,
};

export default ProductAddToCartButton;
